import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import styling from "../dashboards/styles/NewDecisionTree";
import CategorySelection from "./CategorySelection";
import { Questions, QuestionSelection } from "./Question";

import { Button, Typography } from "@mui/material";
import Divider from "@mui/material/Divider";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Box from "@mui/material/Box";
import ClearSharpIcon from "@mui/icons-material/ClearSharp";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";

import { questionsConfig } from "../../helpers/new_ques";
import { API_URL } from "../../config/environment";
import {
  _fetchFilteredIngredients,
  filterDataByField,
  filterDataByMultipleFields,
  filterDataByOccurrence,
  getIngredientsByTechnofunctionality,
} from "../../utils/descisionTreeUtils";
import { getLastKey } from "../../utils/formUtils";
import {
  updateObjectState,
  removeKeysFromObjectState,
} from "../../utils/stateUtils";

export default function DecisionTree({ setResults }) {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();

  // tags that show selections
  const [tags, setTags] = useState([]);
  const [selectedQuestionId, setSelectedQuestionId] = useState(null);
  const [category, setCategory] = useState(null);
  const [ingredientCount, setIngredientCount] = useState(0);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [questionsResults, setQuestionsResults] = useState({});
  const [selectedTags, setSelectedTags] = useState({});

  const handleClick = () => {
    setOpen((value) => !value);
  };

  const handleChipClick = (tag) => {
    setOpen(true);
    setSelectedQuestionId(tag.questionId);
  };

  return (
    <>
      <ClickAwayListener onClickAway={() => setOpen(false)}>
        <Box sx={styling.listener}>
          <Button variant="contained" color="success" onClick={handleClick}>
            {t("dashboard.decision-tree.buttons.filter")}
          </Button>
          {open && (
            <Filters
              setOpen={setOpen}
              setResults={setResults}
              setTags={setTags}
              selectedQuestionId={selectedQuestionId}
              setSelectedQuestionId={setSelectedQuestionId}
              category={category}
              setCategory={setCategory}
              ingredientCount={ingredientCount}
              setIngredientCount={setIngredientCount}
              selectedOptions={selectedOptions}
              setSelectedOptions={setSelectedOptions}
              questionsResults={questionsResults}
              setQuestionsResults={setQuestionsResults}
              selectedTags={selectedTags}
              setSelectedTags={setSelectedTags}
            />
          )}
        </Box>
      </ClickAwayListener>
      {tags.map(
        (tag, index) =>
          tag.option !== "Skip" && (
            <CustomChip
              key={index}
              tag={tag}
              onClick={() => handleChipClick(tag)}
            />
          )
      )}
    </>
  );
}

function Filters({
  setOpen,
  setResults,
  setTags,
  selectedQuestionId,
  setSelectedQuestionId,
  category,
  setCategory,
  ingredientCount,
  setIngredientCount,
  selectedOptions,
  setSelectedOptions,
  questionsResults,
  setQuestionsResults,
  selectedTags,
  setSelectedTags,
}) {
  const { t } = useTranslation();
  const [countError, setCountError] = useState(false);

  // options and selections
  const [clicked, setClicked] = useState(null);
  const [options, setOptions] = useState([]);

  useEffect(() => {
    if (!category) return;
    fetchCount(category, setIngredientCount, setCountError);
  }, [category]);

  const handleApplyButton = async () => {
    let results = questionsResults[getLastKey(questionsResults)] || [];
    if (Object.keys(questionsResults).length === 0 && category) {
      results = await fetchIngredientsByCategory(category);
    }
    setResults(results);
    setTags(Object.values(selectedTags));
    setOpen(false);
  };

  const handleReset = () => {
    setCategory(null);
    setQuestionsResults({});
    setSelectedTags({});
    setTags([]);
    setIngredientCount(0);
    setSelectedQuestionId(null);
    setSelectedOptions({});
    setOptions([]);
    setClicked(false);
  };

  return (
    <Box sx={styling.filters}>
      <ClearSharpIcon sx={styling.exitFilters} onClick={() => setOpen(false)} />
      <FilterContainer
        t={t}
        category={category}
        setCategory={setCategory}
        selectedOptions={selectedOptions}
        setSelectedOptions={setSelectedOptions}
        questionsResults={questionsResults}
        setQuestionsResults={setQuestionsResults}
        setIngredientCount={setIngredientCount}
        setTags={setTags}
        selectedTags={selectedTags}
        setSelectedTags={setSelectedTags}
        selectedQuestionId={selectedQuestionId}
        options={options}
        setOptions={setOptions}
        clicked={clicked}
        setClicked={setClicked}
      />
      <Divider style={styling.dividerColor} />
      <div style={styling.filterButtons}>
        <IngredientCount
          countError={countError}
          ingredientCount={ingredientCount}
        />
        <div>
          <Button
            sx={styling.applyFilter}
            variant="outlined"
            color="success"
            size="medium"
            onClick={handleReset}
          >
            {t("dashboard.ingredientSelection.resetBtnText")}
          </Button>
          <Button
            sx={styling.applyFilter}
            variant="contained"
            color="success"
            size="medium"
            onClick={handleApplyButton}
          >
            {t("dashboard.ingredientSelection.applyBtnText")}
          </Button>
        </div>
      </div>
    </Box>
  );
}

function FilterContainer({
  t,
  category,
  setCategory,
  selectedOptions,
  setSelectedOptions,
  questionsResults,
  setQuestionsResults,
  setIngredientCount,
  setTags,
  selectedTags,
  setSelectedTags,
  selectedQuestionId,
  options,
  setOptions,
  clicked,
  setClicked,
}) {
  // question navigation
  const [currentQuestionId, setCurrentQuestionId] = useState(1);
  const [currentQuestion, setCurrentQuestion] = useState(null);

  // fetched results
  const [ingredientsByCategory, setIngredientsByCategory] = useState({});

  const questions = category
    ? questionsConfig[category].map((q) => ({ ...q, text: t(q.text) }))
    : [];

  useEffect(() => {
    if (selectedQuestionId) {
      setCurrentQuestionId(selectedQuestionId);
    }
  }, [selectedQuestionId]);

  useEffect(() => {
    if (!category) {
      return;
    }

    if (!clicked) {
      setClicked(category);
    }

    const fetchData = async () => {
      try {
        const ingredients = await fetchIngredientsByCategory(category);
        setIngredientsByCategory(ingredients);

        const currentQuestion = questions.find(
          (q) => q.id === currentQuestionId
        );
        if (!currentQuestion) return;

        if (currentQuestion.hasFetchableOptions) {
          await fetchOptions(category, currentQuestionId, setOptions);
        } else {
          setOptions(currentQuestion.options || []);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, [currentQuestionId, category]);

  const handleCategorySelect = (selectedCategory) => {
    setCategory(selectedCategory);
    setCurrentQuestionId(1);
    setSelectedOptions({});
    setTags([]);
    setQuestionsResults({});
    setSelectedTags({});
  };

  const handleOptionSelect = (option) => {
    const nextQuestionId = currentQuestionId + 1;
    const currentQuestion = questions.find((q) => q.id === currentQuestionId);
    setCurrentQuestion(currentQuestion);
    const field = currentQuestion?.field;

    removeKeysFromObjectState(setOptions, questions, nextQuestionId);
    removeKeysFromObjectState(
      setQuestionsResults,
      questions,
      currentQuestionId
    );

    const newSelectedOptions = {
      ...selectedOptions,
      [currentQuestionId]: {
        option: option?.option,
        field: option?.field || field,
      },
    };

    setSelectedOptions(
      updatedSelectedOptions(newSelectedOptions, currentQuestionId)
    );

    setSelectedTags({
      ...selectedTags,
      [currentQuestionId]: {
        question: currentQuestion.text,
        option: option?.option.label,
        questionId: currentQuestionId,
      },
    });

    questionLogic(
      option,
      questionsResults,
      currentQuestionId,
      field,
      category,
      setQuestionsResults,
      ingredientsByCategory,
      questions,
      setIngredientCount
    );

    if (questions.some((q) => q.id === nextQuestionId)) {
      setCurrentQuestionId(nextQuestionId);
    }
  };

  const handleQuestionClick = (questionId) => {
    const allPreviousAnswered = questions.every((q) => {
      return q.id >= questionId || selectedOptions[q.id];
    });

    if (allPreviousAnswered) {
      setCurrentQuestionId(questionId);
    }
  };

  return (
    <div style={styling.filterContainer}>
      <CategorySelection
        t={t}
        clicked={clicked}
        handleClick={(option) => setClicked(clicked === option ? null : option)}
        category={category}
        handleCategorySelect={handleCategorySelect}
      />

      <Divider orientation="vertical" style={styling.dividerColor} />

      <Questions
        selectedQuestionId={currentQuestionId}
        handleQuestionClick={handleQuestionClick}
        questions={questions.map((q) => ({
          ...q,
          answered: !!selectedOptions[q.id],
        }))}
      />

      <Divider orientation="vertical" style={styling.dividerColor} />

      {currentQuestionId && (
        <QuestionSelection
          array={options}
          selectedOptions={selectedOptions}
          onSelect={handleOptionSelect}
          currentField={getFieldById(category, currentQuestionId)}
          questionsResults={questionsResults}
          currentQuestionId={currentQuestionId}
          category={category}
        />
      )}
    </div>
  );
}

// ============================ COMPONENTS ============================
function IngredientCount({ countError, ingredientCount }) {
  return (
    <>
      {countError ? (
        <p style={{ color: "#b06256" }}> Error fetching count </p>
      ) : (
        <p> {ingredientCount} results </p>
      )}
    </>
  );
}

function CustomChip({ tag, onClick }) {
  return (
    <Paper
      elevation={0}
      sx={styling.customChip}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      style={{ cursor: "pointer" }}
    >
      <Box sx={styling.chipQuestion}>
        <Typography variant="body2"> {tag.question} </Typography>
      </Box>
      <Box sx={styling.chipOption}>
        <Typography variant="body2"> {tag.option} </Typography>
      </Box>
    </Paper>
  );
}

// ============================ HELPERS ============================
const categoryCodePrefixes = {
  substitution_de_matiere_grasse: "FTS-",
  selection_des_antimicrobiens: "ANM-",
  substitution_des_sucres: "SGS-",
  selection_des_antioxydants: "ANX-",
};

async function questionLogic(
  option,
  questionsResults,
  currentQuestionId,
  field,
  category,
  setQuestionsResults,
  ingredientsByCategory,
  questions,
  setIngredientCount
) {
  const isComplexFilter = questions[currentQuestionId - 1]?.isComplexFilter;
  const isFilterByOccurrence =
    questions[currentQuestionId - 1]?.isFilterByOccurrence;
  const relatedColumns = Array.isArray(option.option.value)
    ? option.option.value
    : [option.option.value];

  if (option.option.value !== "Skip") {
    if (currentQuestionId === 1) {
      const count = await firstQuestion(
        field,
        relatedColumns,
        category,
        currentQuestionId,
        setQuestionsResults
      );
      setIngredientCount(count);
      firstQuestion(
        field,
        relatedColumns,
        category,
        currentQuestionId,
        setQuestionsResults
      );
    } else {
      otherQuestions(
        questionsResults,
        setQuestionsResults,
        currentQuestionId,
        ingredientsByCategory,
        questions,
        relatedColumns,
        field,
        isFilterByOccurrence,
        isComplexFilter,
        setIngredientCount
      );
    }
  } else {
    removeKeysFromObjectState(
      setQuestionsResults,
      questions,
      currentQuestionId
    );
    if (currentQuestionId === 1 && Object.keys(questionsResults).length === 0) {
      firstQuestion(null, [], category, currentQuestionId, setQuestionsResults);
    }
  }
}

async function firstQuestion(
  field,
  relatedColumns,
  category,
  currentQuestionId,
  setQuestionsResults
) {
  let data;

  if (!field || !relatedColumns || relatedColumns.length === 0) {
    data = await fetchIngredientsByCategory(category);
  } else {
    category = checkEmulsifiants(relatedColumns, category);
    category = checkMoussants(relatedColumns, category);
    category = checkEpaississants(relatedColumns, category);
    data = await fetchFilteredIngredients(field, relatedColumns, category);
  }
  updateObjectState(setQuestionsResults, currentQuestionId, data);
  // setIngredientCount(data.length);
  return data.length;
}

function checkEmulsifiants(relatedColumns, category) {
  if (relatedColumns[0] === "Agent emulsifiant/Stabilisateur d'emulsion") {
    category = "selection_des_emulsifiants_stabilisateurs";
  } else if (relatedColumns[0] === "Stabilisateur d'emulsion") {
    category = "selection_des_stabilisateurs_emulsions";
  } else {
    return category;
  }
  return category;
}

function checkMoussants(relatedColumns, category) {
  if (relatedColumns[0] === "Agent moussant/Stabilisateur de mousse") {
    category = "selection_des_moussants_stabilisateurs";
  } else if (relatedColumns[0] === "Stabilisateur de mousse") {
    category = "selection_des_stabilisateurs_mousses";
  } else {
    return category;
  }
  return category;
}

function checkEpaississants(relatedColumns, category) {
  if (relatedColumns[0] === "Agent gelifiant") {
    category = "selection_des_gelifiants";
  } else if (relatedColumns[0] === "Agent gelifiant/Epaississant") {
    category = "selection_des_epaississants_gelifiants";
  } else {
    return category;
  }
  return category;
}
function otherQuestions(
  questionsResults,
  setQuestionsResults,
  currentQuestionId,
  ingredientsByCategory,
  questions,
  relatedColumns,
  field,
  isFilterByOccurrence,
  isComplexFilter,
  setIngredientCount
) {
  const questionResultsLength = Object.keys(questionsResults).length;

  let lastQuestionResultsIndex = null;

  if (questionResultsLength !== currentQuestionId) {
    if (getLastKey(questionsResults) > currentQuestionId) {
      delete questionsResults[getLastKey(questionsResults)];
    } else {
      delete questionsResults[currentQuestionId];
    }
    lastQuestionResultsIndex = getLastKey(questionsResults);
  } else {
    lastQuestionResultsIndex = currentQuestionId - 1;
  }

  let data = null;
  let _questionsResults = null;

  if (lastQuestionResultsIndex) {
    _questionsResults = questionsResults[lastQuestionResultsIndex];
  } else {
    _questionsResults = ingredientsByCategory;
  }

  if (isFilterByOccurrence) {
    data = filterDataByOccurrence(
      _questionsResults,
      questions[currentQuestionId - 1].valueFields,
      relatedColumns
    );
  }

  if (isComplexFilter) {
    data = filterDataByMultipleFields(
      _questionsResults,
      questions[currentQuestionId - 1].properties,
      relatedColumns,
      questions[currentQuestionId - 1].propertyFields,
      questions[currentQuestionId - 1].valueFields
    );
  }

  if (!isComplexFilter && !isFilterByOccurrence) {
    data = filterDataByField(_questionsResults, field, relatedColumns);
  }

  // if (data.length === 0) {
  //   alert(
  //     "Your selection does not have any data, please try another selection."
  //   );
  // }

  updateObjectState(
    setQuestionsResults,
    currentQuestionId,
    data.length > 0 ? data : []
  );
  setIngredientCount(data.length);
}

async function fetchFilteredIngredients(field, relatedColumns, category) {
  const url = `${API_URL}/filter-options/filtered-data`;

  return await _fetchFilteredIngredients(url, field, relatedColumns, category);
}

function updatedSelectedOptions(newSelectedOptions, currentQuestionId) {
  return Object.keys(newSelectedOptions)
    .filter((currQuestionId) => parseInt(currQuestionId) <= currentQuestionId)
    .reduce((newOptions, key) => {
      newOptions[key] = newSelectedOptions[key];
      return newOptions;
    }, {});
}

async function fetchIngredientsByCategory(category) {
  return await getIngredientsByTechnofunctionality(category);
}

function getFieldById(category, id) {
  if (category === null) return;

  const questionsByCategory = questionsConfig[category];
  const question = questionsByCategory.find((q) => q.id === id);

  return question.field;
}

async function fetchCount(category, setIngredientCount, setCountError) {
  try {
    setCountError(false);
    const ingredients = await getIngredientsByTechnofunctionality(category);
    setIngredientCount(ingredients.length);
  } catch (err) {
    console.error("Failed to fetch ingredients by category:", err);
    setCountError(true);
  }
}

async function fetchOptions(category, selectedQuestionId, setOptions) {
  try {
    const field = getFieldById(category, selectedQuestionId);
    const url = `${API_URL}/filter-options`;

    const response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        questionId: selectedQuestionId,
        field: field,
        technofunctionality: category,
      }),
    });

    if (!response.ok) {
      throw new Error("Failed to fetch options");
    }

    const optionResults = await response.json();
    setOptions(optionResults);
  } catch (error) {
    console.error("Error fetching options:", error);
    setOptions({});
  }
}
